<template>
  <ChartCard :data-widget-id="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="hiring-source-categories"
    />
    <ChartContent
      height="293.5px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBarChart"
      class="px-3 pt-2 pb-0"
    >
      <BarChart :id="widgetId" :data="chartData" :options="chartOptions" :plugins="plugins" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { calculatePercent } from '@/components/insights/helpers';
import {
  dataLabelsConfig,
  flippedAxisTooltipPluginConfig,
  xScaleWithPercentFormatConfig,
  basicYScaleConfig,
} from '@/api/helpers/insights/commonChartConfigs';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'What source categories do our hires come from?';

const plugins = [ChartDataLabels];

const widgetId = 'hire-source-category';

const {
  sourceInternal,
  sourceOutbound,
  sourceReferral,
  sourceInbound,
  sourceOther,
} = useColors();

const noWidgetData = computed(() => {
  const { sources } = props.data || {};
  return !sources
    || Object.keys(sources).length === 0
    || Object.values(sources).every((value) => value === 0);
});

const chartData = computed(() => {
  const { sources } = props.data || {};
  const {
    inbound: totalInbound,
    internal: totalInternal,
    referrals: totalReferrals,
    other: totalOther,
    outbound: totalOutbound,
  } = sources || {};

  const overallTotal = totalInbound + totalInternal + totalReferrals + totalOther + totalOutbound;

  const dataMapping = [
    {
      label: 'Inbound',
      value: totalInbound,
      color: sourceInbound.value,
    },
    {
      label: 'Internal',
      value: totalInternal,
      color: sourceInternal.value,
    },
    {
      label: 'Outbound',
      value: totalOutbound,
      color: sourceOutbound.value,
    },
    {
      label: 'Referrals',
      value: totalReferrals,
      color: sourceReferral.value,
    },
    {
      label: 'Other',
      value: totalOther,
      color: sourceOther.value,
    },
  ].sort((a, b) => b.value - a.value);

  return {
    labels: dataMapping.map((item) => item.label),
    datasets: [
      {
        label: '',
        backgroundColor: dataMapping.map((item) => item.color),
        borderColor: dataMapping.map((item) => item.borderColor),
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data: dataMapping.map(({ value }) => calculatePercent(value, overallTotal)),
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: flippedAxisTooltipPluginConfig(chartData, widgetId),
    datalabels: dataLabelsConfig,
  },
  scales: {
    x: xScaleWithPercentFormatConfig,
    y: basicYScaleConfig,
  },
}));
</script>
